import { api } from "api/core";
import type { ApiResponseError } from "api/types";

import type { Message, Thread } from "./component/types";
import type { CitationDetailsDto } from "./widget/dtos/citationDetailsDto";
import type { MessageCitationDto } from "./widget/dtos/messageCitationDto";
import type { MessageDto } from "./widget/dtos/messageDto";
import type { ThreadDto } from "./widget/dtos/threadDto";
import * as MessageMapper from "./widget/mappers/messageMapper";
import * as ThreadMapper from "./widget/mappers/threadMapper";
import AnalyticsUtil from "ee/utils/AnalyticsUtil";
import { getAppsmithConfigs } from "ee/configs";
import { startRootSpan } from "instrumentation/generateTraces";
import { appsmithTelemetry } from "instrumentation";
const baseUrl = "/v1/ai";
const appsmithConfig = getAppsmithConfigs();
const segmentEnabled = appsmithConfig.segment.enabled;

const traceAndLogAIOperation = async <T>(
  operationName: string,
  operation: () => Promise<T>,
): Promise<T> => {
  const startTime = performance.now();
  const span = startRootSpan(operationName);

  try {
    const result = await operation();
    const timeToFirstChunk = performance.now() - startTime;

    // Log the time to first chunk using Faro
    appsmithTelemetry.captureMeasurement(
      {
        first_chunk_time: timeToFirstChunk,
      },
      {
        operationName,
      },
    );

    span.end();

    return result;
  } catch (error) {
    span.end();
    throw error;
  }
};

interface ThreadRunCreatedStreamLine {
  event: "thread.run.created"; // discriminator
  data: {
    threadId: string;
  };
}

interface ThreadMessageCreatedStreamLine {
  event: "thread.message.created"; // discriminator
  data: {
    id: string;
    createdAt: number;
    role: "user" | "assistant" | "error";
    type: "text";
    delta: {
      content: Array<{
        text: {
          value: string;
        };
        type: "text";
      }>;
    };
  };
}

export interface ThreadToolCallRequestStreamLine {
  event: "thread.run.tool_call_request"; // discriminator
  data: {
    runId: string;
    threadId: string;
    prevMessageId: string;
    prevResponseId: string;
    actions: {
      isApprovalRequired: boolean;
      toolCallId: string;
      function: {
        arguments: string;
        entityId: string;
        entityType: "Query" | "JSFunction";
        name: string;
        iconLocation: string;
        output?: string;
      };
    }[];
  };
}

interface ThreadMessageDeltaStreamLine {
  event: "thread.message.delta"; // discriminator
  data: {
    threadId: string;
    runId: string;
    messageId: string;
    delta: {
      content: Array<{
        text: {
          value: string;
        };
        type: "text";
      }>;
    };
  };
}

interface ThreadCitationsContentStreamLine {
  event: "citations.content"; // discriminator
  data: {
    threadId: string;
    runId: string;
    messageId: string;
    citations: Array<MessageCitationDto>;
  };
}

interface ThreadCitationsRefStreamLine {
  event: "citations.ref"; // discriminator
  data: {
    threadId: string;
    runId: string;
    messageId: string;
    refs: string;
  };
}

interface ThreadErrorStreamLine {
  event: "error"; // discriminator
  data: ApiResponseError;
}

export type AiAssistantStreamLine =
  | ThreadRunCreatedStreamLine
  | ThreadMessageCreatedStreamLine
  | ThreadToolCallRequestStreamLine
  | ThreadMessageDeltaStreamLine
  | ThreadCitationsContentStreamLine
  | ThreadCitationsRefStreamLine
  | ThreadErrorStreamLine;

interface GetThreadsParams {
  queryId: string;
  widgetId: string;
}

export const fetchThreads = async ({
  queryId,
  widgetId,
}: GetThreadsParams): Promise<Thread[]> => {
  const anonymousId = AnalyticsUtil.getAnonymousId();

  const response = await api.get<{ body: { activeThreads: ThreadDto[] } }>(
    `${baseUrl}/${queryId}/threads`,
    {
      params: {
        userName: segmentEnabled ? anonymousId : undefined,
        resourceId: widgetId,
        provider: "openai", // TODO: Remove this once BE have a way to get the provider from the query
      },
    },
  );

  if (!response.responseMeta.success) {
    return Promise.reject(response.responseMeta.error);
  }

  return response.data.body.activeThreads.map(ThreadMapper.fromDto);
};

interface GetMessagesParams {
  threadId: string;
  queryId: string;
  widgetId: string;
}

export const fetchMessages = async ({
  queryId,
  threadId,
  widgetId,
}: GetMessagesParams): Promise<Message[]> => {
  const anonymousId = AnalyticsUtil.getAnonymousId();

  const response = await api.get<{
    body: { messages: { content: MessageDto[] } };
  }>(`${baseUrl}/${queryId}/threads/${threadId}/messages`, {
    params: {
      userName: segmentEnabled ? anonymousId : undefined,
      resourceId: widgetId,
      provider: "openai", // TODO: Remove this once BE have a way to get the provider from the query
      limit: 30,
      order: "DESC",
    },
  });

  if (!response.responseMeta.success) {
    return Promise.reject(response.responseMeta.error);
  }

  return response.data.body.messages.content.map(MessageMapper.fromDto);
};

interface SendMessageRequestBody {
  requestType: string;
  config: {
    threadId: string;
    resourceId: string;
    actionId: string;
    userName?: string | null;
    prevResponseId?: string;
  };
  message: string;
  viewMode: boolean;
}

export const sendMessage = async (
  requestBody: SendMessageRequestBody,
  headers: Headers,
  signal?: AbortSignal,
): Promise<Response> => {
  return traceAndLogAIOperation("appsmith_ai_chat", async () => {
    const request = new Request(`/api${baseUrl}/invoke`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(requestBody),
      signal,
    });

    return fetch(request);
  });
};

interface FetchCitationParams {
  queryId: string;
  citationId: string;
  messageId: string;
  threadId: string;
  viewMode: boolean;
}

export const fetchCitation = async ({
  citationId,
  messageId,
  queryId,
  threadId,
  viewMode,
}: FetchCitationParams) => {
  const anonymousId = AnalyticsUtil.getAnonymousId();
  const response = await api.get<{ data: CitationDetailsDto }>(
    `${baseUrl}/citation`,
    {
      params: {
        userName: segmentEnabled ? anonymousId : undefined,
        citationId,
        messageId,
        threadId,
        actionId: queryId,
        viewMode,
      },
    },
  );

  if (!response.responseMeta.success) {
    return Promise.reject(response.responseMeta.error);
  }

  return response;
};

interface CreateThreadParams {
  widgetId: string;
  queryId: string;
}

export const createThread = async ({
  queryId,
  widgetId,
}: CreateThreadParams) => {
  const anonymousId = AnalyticsUtil.getAnonymousId();

  const response = await api.post<{
    body: { thread: { id: string } };
  }>(`${baseUrl}/${queryId}/threads`, {
    resourceId: widgetId,
    config: {
      userName: segmentEnabled ? anonymousId : undefined,
    },
  });

  if (!response.responseMeta.success) {
    return Promise.reject(response.responseMeta.error);
  }

  return response.data.body.thread.id;
};

interface SubmitActionRequestOutputRequestBody {
  config: {
    resourceId: string;
    threadId: string;
    runId: string;
    prevMessageId: string;
    userName?: string | null;
    prevResponseId?: string;
  };
  outputs: {
    toolCallId: string;
    name: string;
    data: string;
  }[];
  viewMode: boolean;
}

export const submitActionRequestOutput = async (
  requestBody: SubmitActionRequestOutputRequestBody,
  queryId: string,
  headers: Headers,
  signal?: AbortSignal,
) => {
  const request = new Request(`/api${baseUrl}/${queryId}/tool-outputs`, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(requestBody),
    signal,
  });

  return traceAndLogAIOperation("appsmith_ai_tool_outputs", async () =>
    fetch(request),
  );
};

export const cancelRun = async (
  actionId: string,
  threadId: string,
  runId = "foo",
  viewMode = false,
) => {
  const anonymousId = AnalyticsUtil.getAnonymousId();

  return await api.post(
    `${baseUrl}/${actionId}/threads/${threadId}/runs/${runId}/cancel`,
    {
      config: {
        actionId,
        userName: segmentEnabled ? anonymousId : undefined,
      },
      viewMode,
    },
  );
};
